<template>
    <div>
        <v-container fluid>
            <v-row>
                <v-col sm="6" md="3">
                    <search-bar
                        v-model="query.search"
                        v-on:request-search="onRequestSearch"
                    />
                </v-col>
                <v-col sm="6" md="3">
                    <v-autocomplete
                        solo
                        clearable
                        hide-details
                        label="Restaurant"
                        item-text="name"
                        item-value="id"
                        v-model="query.restaurant_id"
                        v-bind:items="$store.state.app.staticData.restaurants"
                        v-on:change="onRequestRestaurantFilter"
                    />
                </v-col>
                <v-col sm="6" md="3">
                    <sort-bar
                        v-model="query.sort"
                        v-bind:items="sortItems"
                        v-on:request-sort="onRequestSort"
                    />
                </v-col>
                <v-col sm="6" md="3">
                    <filter-bar
                        label="Order Statuses"
                        v-model="query.order_statuses"
                        v-bind:items="statusFilterItems"
                        v-on:request-filter="onRequestStatusFilter"
                    />
                </v-col>
            </v-row>
        </v-container>
        <v-pagination
            v-model="pagination.currentPage"
            v-bind:length="pagination.pageCount"
            v-on:input="onRequestPageChange"
        />
        <p class="mt-2 text--secondary text-center text-subtitle-2">
            {{ pagination.pageItemFrom }} - {{ pagination.pageItemTo }} of
            {{ pagination.totalItems }}
        </p>
        <v-row justify="center">
            <v-col cols="12" md="8" lg="6">
                <restaurantorder-list-item
                    class="mb-4"
                    v-for="item in orders"
                    v-bind:key="item.id"
                    v-bind:order="item"
                    v-on:request-show="showOrder"
                    v-on:request-edit="editOrder"
                    v-on:request-delete="deleteOrder"
                />
            </v-col>
        </v-row>
        <v-pagination
            v-model="pagination.currentPage"
            v-bind:length="pagination.pageCount"
            v-on:input="onRequestPageChange"
        />
    </div>
</template>

<script>
import orderApi from '@/api/orderApi';
import * as orderStatuses from '@/constants/orderStatuses';
import * as orderTypes from '@/constants/orderTypes';
import snackbar from '@/utils/snackbar';

export default {
    components: {
        'restaurantorder-list-item': () =>
            import('@/views/op/RestaurantOrderListItem.vue'),
        'search-bar': () => import('@/components/SearchBar.vue'),
        'sort-bar': () => import('@/components/SortBar.vue'),
        'filter-bar': () => import('@/components/FilterBar.vue')
    },
    data() {
        return {
            orders: [],
            query: {
                search: '',
                restaurant_id: null,
                sort: '',
                order_statuses: []
            },
            pagination: {
                currentPage: 1,
                pageItemFrom: 0,
                pageItemTo: 0,
                totalItems: 0,
                pageCount: 0
            },
            sortItems: [
                { text: 'Newest first', value: 'created_at:desc' },
                { text: 'Oldest first', value: 'created_at:asc' }
            ],
            statusFilterItems: [
                { text: 'New orders', value: orderStatuses.NEW },
                { text: 'Assigned orders', value: orderStatuses.ASSIGNED },
                {
                    text: 'In-progress orders',
                    value: orderStatuses.IN_PROGRESS
                },
                { text: 'Completed orders', value: orderStatuses.COMPLETED },
                { text: 'Cancelled orders', value: orderStatuses.CANCELLED }
            ]
        };
    },
    async beforeRouteEnter(to, from, next) {
        const response = await orderApi.getOrders({
            ...to.query,
            type: orderTypes.RESTAURANT
        });
        next(vm => vm.setData(response.data.orders, { ...to.query }));
    },
    async beforeRouteUpdate(to, from, next) {
        const response = await orderApi.getOrders({
            ...to.query,
            type: orderTypes.RESTAURANT
        });
        this.setData(response.data.orders, { ...to.query });
        next();
    },
    methods: {
        showOrder(order) {
            this.$router.push({
                name: 'op-restaurantorder-show',
                params: {
                    order_id: order.id
                }
            });
        },
        editOrder(order) {
            this.$router.push({
                name: 'op-restaurantorder-edit',
                params: {
                    order_id: order.id
                }
            });
        },
        async deleteOrder(order) {
            const confirmed = confirm('Confirm deletion');
            if (!confirmed) return;
            await orderApi.deleteOrder(order.id);
            // Avoid reloading the page. Note that router.go() reloads
            this.onRequestPageChange(this.pagination.currentPage);
            snackbar.show('Deleted successfully', 'success');
        },
        // Expecting paginated to be response.data.orders
        setData(paginated, query) {
            this.query = query;

            // Hack to trigger changed event
            if (null != query.restaurant_id) {
                this.query.restaurant_id = parseInt(query.restaurant_id);
            }

            this.orders = paginated.data;
            this.pagination.totalItems = paginated.total;
            this.pagination.currentPage = paginated.current_page;
            this.pagination.pageItemFrom = paginated.from;
            this.pagination.pageItemTo = paginated.to;
            this.pagination.pageCount = paginated.last_page;
        },
        async onRequestSearch() {
            // Use only search on new search
            const params = { search: this.query.search };
            await this.request(params);
            this.query.sort = null; // Reset sort
            this.query.order_statuses = []; // Reset status orders
            this.query.restaurant_id = null; // Reset restaurant
        },
        async onRequestRestaurantFilter() {
            // Don't use page on new filter
            await this.request(this.query);
        },
        async onRequestSort() {
            // Don't use page on new sort
            await this.request(this.query);
        },
        async onRequestStatusFilter() {
            // Don't use page on new filter
            await this.request(this.query);
        },
        async onRequestPageChange(newPage) {
            const params = {
                ...this.query,
                page: newPage
            };
            await this.request(params);
        },
        async request(params) {
            this.orders = [];
            this.$router.push({ query: params });
        }
    }
};
</script>
